import React, { useMemo, useContext, useState, useRef } from 'react';
import { CKEditor } from 'ckeditor4-react';
import { FieldError, FieldInfo } from '../../../../hybrid/wrappers';
import FromInputTitle from '../FormFieldTitle/FormFieldTitle';
import UserContext from '../../../../common/utils/getContext';
import PropTypes from 'prop-types';
import css from './FormEditor.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../common/utils/styles';

const Editor = ({ compStyle, attribute, value, errors, title, setFormData, required, hint, type, info, maxLength = 5000 }) => {
    const { stylers, classes } = generateCompStylers(compStyle);
    const [content, setContent] = useState(value);
    const { lang, userToken } = useContext(UserContext);
    const config = useMemo(
        () => ({
            clipboard_handleImages: false,
            allowedContent: true,
            uiColor: 'var(--whiteColor)',
            language: lang,
            versionCheck: false,
            filebrowserUploadUrl: '/api/files',
            extraPlugins: 'justify',
            fileTools_requestHeaders: {
                ...(userToken ? { Authorization: `Bearer ${userToken}` } : {})
            },
            toolbar: [
                { name: 'style', items: ['Format'] },
                {
                    name: 'basicstyles',
                    items: [
                        'Bold',
                        'Italic',
                        'Underline',
                        'Strike',
                        'Subscript',
                        'Superscript',
                        '-',
                        'CopyFormatting',
                        'RemoveFormat'
                    ]
                },
                { name: 'document', items: ['Source', 'Image'] },
                { name: 'links', items: ['Link'] },
                {
                    name: 'paragraph',
                    items: ['BulletedList', 'NumberedList', 'Blockquote', 'JustifyLeft', 'JustifyCenter', 'JustifyRight']
                },
                { name: 'insert', items: ['Table', ''] },
                { name: 'clipboard', items: ['Undo', 'Redo'] },
                {
                    name: 'tools',
                    items: ['Maximize']
                }
            ]
        }),
        [userToken, lang]
    );

    const editorRef = useRef(null);

    const handleEditorChange = () => {
        if (editorRef.current) {
            const data = editorRef.current.getData();
            const text = editorRef.current.document.getBody().getText();

            if (text.length > maxLength && 0) {
                const truncatedData = text.slice(0, maxLength);
                editorRef.current.setData(truncatedData, {
                    callback: () => {
                        // Встановлюємо курсор в кінець після обрізання
                        editorRef.current.focus();
                        const range = editorRef.current.createRange();
                        range.moveToElementEditEnd(editorRef.current.document.getBody());
                        editorRef.current.getSelection().selectRanges([range]);
                    }
                });
            } else {
                setContent(data);
                setFormData(attribute, data);
            }
        }
    };

    const handleInstanceReady = (evt) => {
        editorRef.current = evt.editor;

        // Використовуємо таймер для обмеження частоти викликів
        let timeoutId;
        evt.editor.on('change', () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleEditorChange, 0);
        });
    };

    return (
        <div style={stylers.wrapper} className={classnames(css.formGroup, css['separated_title'], classes.wrapper || {})}>
            {!!title && (
                <FromInputTitle
                    type={type}
                    errors={errors}
                    title={title}
                    compStyle={compStyle}
                    hint={hint}
                    required={required}
                    variationStyle={'separated_title'}
                />
            )}
            <CKEditor initData={content} onInstanceReady={handleInstanceReady} config={config} />
            {!!info && <FieldInfo compStyle={compStyle?.info}>{info}</FieldInfo>}
            {errors && errors.length
                ? errors.map((err, idx) => (
                      <FieldError className={compStyle?.error} key={`${attribute}${idx}`}>
                          {err}
                      </FieldError>
                  ))
                : null}
        </div>
    );
};
Editor.propTypes = {
    compStyle: PropTypes.object,
    attribute: PropTypes.string,
    value: PropTypes.string,
    errors: PropTypes.array,
    title: PropTypes.object,
    setFormData: PropTypes.func,
    required: PropTypes.bool,
    hint: PropTypes.node,
    type: PropTypes.string,
    info: PropTypes.node
};
export default Editor;
